import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  SvgIcon,
} from '@mui/material';
import {
  CheckCircle,
  CircleAlert,
  Info,
  OctagonAlert,
  X,
} from 'lucide-react';
import { useUIStore } from '../../store';

/**
 * Global confirmation dialog component
 */
const ConfirmDialog = () => {
  const { confirmDialog, closeConfirmDialog } = useUIStore();
  const {
    open,
    title,
    message,
    confirmText,
    cancelText,
    confirmColor,
    severity,
    onConfirm,
    onCancel,
  } = confirmDialog;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    closeConfirmDialog();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    closeConfirmDialog();
  };

  const getSeverityIcon = () => {
    switch (severity) {
      case 'error':
        return <SvgIcon component={OctagonAlert} inheritViewBox sx={{ fontSize: 48, color: 'error.main' }} />;
      case 'warning':
        return <SvgIcon component={CircleAlert} inheritViewBox sx={{ fontSize: 48, color: 'warning.main' }} />;
      case 'success':
        return <SvgIcon component={CheckCircle} inheritViewBox sx={{ fontSize: 48, color: 'success.main' }} />;
      case 'info':
      default:
        return <SvgIcon component={Info} inheritViewBox sx={{ fontSize: 48, color: 'info.main' }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 0, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getSeverityIcon()}
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={handleCancel}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <SvgIcon component={X} inheritViewBox />
      </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mt: 1 }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} variant="outlined" color="inherit">
          {cancelText || 'Cancel'}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={confirmColor || 'primary'}
          autoFocus
        >
          {confirmText || 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Standalone confirm dialog component
 */
export const ConfirmDialogStandalone = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm',
  message = 'Are you sure?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  severity = 'warning',
  loading = false,
}) => {
  const getSeverityIcon = () => {
    switch (severity) {
      case 'error':
        return <SvgIcon component={OctagonAlert} inheritViewBox sx={{ fontSize: 48, color: 'error.main' }} />;
      case 'warning':
        return <SvgIcon component={CircleAlert} inheritViewBox sx={{ fontSize: 48, color: 'warning.main' }} />;
      case 'success':
        return <SvgIcon component={CheckCircle} inheritViewBox sx={{ fontSize: 48, color: 'success.main' }} />;
      case 'info':
      default:
        return <SvgIcon component={Info} inheritViewBox sx={{ fontSize: 48, color: 'info.main' }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 0, pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getSeverityIcon()}
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <SvgIcon component={X} inheritViewBox />
      </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mt: 1 }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading}
          autoFocus
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
